import type { TimeSignature } from './meter.ts';
import { phraseMeter, tupletAt, type RhythmPhrase } from './phrase.ts';

/**
 * Notation: turns the rhythm (sounds and silences) into written notes and rests.
 * A sound that cannot be written as one note — because it crosses a barline, or
 * crosses a beat from an off-beat, or has no single note value — is split into
 * several notes joined by ties. Rests are split the same way but never tied.
 */

export type NoteValueCode = 'w' | 'h' | 'q' | '8' | '16';

interface WrittenValue {
  ticks: number;
  code: NoteValueCode;
  dots: 0 | 1;
}

/** Writable durations, longest first. */
export const WRITTEN_VALUES: readonly WrittenValue[] = [
  { ticks: 192, code: 'w', dots: 0 },
  { ticks: 144, code: 'h', dots: 1 },
  { ticks: 96, code: 'h', dots: 0 },
  { ticks: 72, code: 'q', dots: 1 },
  { ticks: 48, code: 'q', dots: 0 },
  { ticks: 36, code: '8', dots: 1 },
  { ticks: 24, code: '8', dots: 0 },
  { ticks: 12, code: '16', dots: 0 },
];

const VALUE_BY_TICKS: ReadonlyMap<number, WrittenValue> = new Map(WRITTEN_VALUES.map((v) => [v.ticks, v]));

export function writtenValue(ticks: number): WrittenValue | undefined {
  return VALUE_BY_TICKS.get(ticks);
}

export interface NotationItem {
  /** Ticks from the start of the phrase. */
  start: number;
  duration: number;
  code: NoteValueCode;
  dots: 0 | 1;
  rest: boolean;
  /** This note is tied to the next item (it continues the same sound). */
  tieToNext: boolean;
  /** This note continues the previous item's sound: it must not be tapped. */
  tiedFromPrevious: boolean;
  /** Index of the rhythm event this item writes. */
  eventIndex: number;
  /** Set when the item belongs to a triplet: its written value is 3⁄2 of its real length. */
  tuplet?: true;
}

export interface EngravedMeasure {
  index: number;
  items: NotationItem[];
  /** Groups of item indexes (within `items`) to beam together. */
  beams: number[][];
  /** Groups of item indexes (within `items`) written as a triplet (3 in the time of 2). */
  tuplets: number[][];
}

/**
 * Split one sound or silence that lies inside a single measure into writable
 * pieces. `start`/`end` are ticks from the start of that measure.
 */
export function splitWithinMeasure(ts: TimeSignature, start: number, end: number): { start: number; duration: number }[] {
  const pieces: { start: number; duration: number }[] = [];
  let cursor = start;
  while (cursor < end) {
    const beatStart = Math.floor(cursor / ts.beatTicks) * ts.beatTicks;
    const offset = cursor - beatStart;
    const remaining = end - cursor;
    let chosen: number | undefined;
    if (offset === 0) {
      // On a beat: a note may cover whole beats (half, dotted half, whole…), or
      // one and a half beats as a dotted value (dotted quarter in x/4), or part of a beat.
      chosen = WRITTEN_VALUES.find(
        (v) =>
          v.ticks <= remaining &&
          (v.ticks % ts.beatTicks === 0 || v.ticks < ts.beatTicks || (v.dots === 1 && v.ticks === ts.beatTicks * 1.5)),
      )?.ticks;
    } else {
      // Off the beat: never cross the next beat (that is what ties are for). A dotted
      // value must end on the beat. In 2/2 the quarter-note group plays that role
      // inside the half-note beat, so a dotted eighth that starts or ends on a
      // quarter is written as is (the groups equal the beat in every other meter).
      const limit = Math.min(remaining, ts.beatTicks - offset);
      chosen = WRITTEN_VALUES.find(
        (v) =>
          v.ticks <= limit &&
          (v.dots === 0
            ? offset % 12 === 0
            : (offset + v.ticks) % ts.beamTicks === 0 || (offset % ts.beamTicks === 0 && v.ticks < ts.beamTicks)),
      )?.ticks;
    }
    const duration = chosen ?? 12;
    pieces.push({ start: cursor, duration });
    cursor += duration;
  }
  return pieces;
}

/** Write a phrase as notation, one entry per measure. */
export function engravePhrase(phrase: RhythmPhrase): EngravedMeasure[] {
  const ts = phraseMeter(phrase);
  const measures: EngravedMeasure[] = Array.from({ length: phrase.measures }, (_, index) => ({ index, items: [], beams: [], tuplets: [] }));
  phrase.events.forEach((event, eventIndex) => {
    // Inside a triplet an event is one written note (never split or tied): 16 ticks reads as an eighth, 32 as a quarter.
    if (tupletAt(phrase, event.start)) {
      const value = writtenValue((event.duration * 3) / 2) as WrittenValue;
      measures[Math.floor(event.start / ts.measureTicks)]!.items.push({
        start: event.start,
        duration: event.duration,
        code: value.code,
        dots: value.dots,
        rest: event.rest,
        tieToNext: false,
        tiedFromPrevious: false,
        eventIndex,
        tuplet: true,
      });
      return;
    }
    const pieces: NotationItem[] = [];
    let cursor = event.start;
    const end = event.start + event.duration;
    while (cursor < end) {
      const m = Math.floor(cursor / ts.measureTicks);
      const measureStart = m * ts.measureTicks;
      const segmentEnd = Math.min(end, measureStart + ts.measureTicks);
      for (const piece of splitWithinMeasure(ts, cursor - measureStart, segmentEnd - measureStart)) {
        const value = writtenValue(piece.duration) as WrittenValue;
        pieces.push({
          start: measureStart + piece.start,
          duration: piece.duration,
          code: value.code,
          dots: value.dots,
          rest: event.rest,
          tieToNext: false,
          tiedFromPrevious: false,
          eventIndex,
        });
      }
      cursor = segmentEnd;
    }
    if (!event.rest) {
      pieces.forEach((p, i) => {
        p.tieToNext = i < pieces.length - 1;
        p.tiedFromPrevious = i > 0;
      });
    }
    for (const p of pieces) measures[Math.floor(p.start / ts.measureTicks)]!.items.push(p);
  });
  for (const measure of measures) {
    measure.beams = beamGroups(ts, measure.items);
    measure.tuplets = (phrase.tuplets ?? [])
      .map((t) => measure.items.flatMap((item, i) => (item.tuplet && item.start >= t.start && item.start < t.start + t.duration ? [i] : [])))
      .filter((group) => group.length > 0);
  }
  return measures;
}

/** Beam consecutive eighth-or-shorter notes that fall in the same beam group; rests break beams. */
function beamGroups(ts: TimeSignature, items: NotationItem[]): number[][] {
  const groups: number[][] = [];
  let current: number[] = [];
  let currentGroup = -1;
  const flush = () => {
    if (current.length >= 2) groups.push(current);
    current = [];
  };
  items.forEach((item, index) => {
    // By written value: a triplet quarter (32 ticks) is still a quarter and takes no beam.
    const beamable = !item.rest && (item.code === '8' || item.code === '16');
    const group = Math.floor((item.start % ts.measureTicks) / ts.beamTicks);
    const endGroup = Math.floor(((item.start % ts.measureTicks) + item.duration - 1) / ts.beamTicks);
    if (!beamable || group !== endGroup) {
      flush();
      currentGroup = -1;
      return;
    }
    if (group !== currentGroup) {
      flush();
      currentGroup = group;
    }
    current.push(index);
  });
  flush();
  return groups;
}

/** Tick positions of tied continuations: written notes that must NOT be tapped. */
export function tieContinuationTicks(measures: EngravedMeasure[]): number[] {
  return measures.flatMap((m) => m.items.filter((i) => i.tiedFromPrevious).map((i) => i.start));
}

/** Rebuild the rhythm from notation (used to prove engraving never changes the music). */
export function rhythmFromNotation(measures: EngravedMeasure[]): { start: number; duration: number; rest: boolean }[] {
  // Tied notes join into one sound; the pieces of one silence (split at beats or barlines) join too.
  const out: { start: number; duration: number; rest: boolean }[] = [];
  let lastEvent = -1;
  for (const item of measures.flatMap((m) => m.items)) {
    const last = out[out.length - 1];
    const continues = last && (item.tiedFromPrevious || (item.rest && last.rest && item.eventIndex === lastEvent));
    if (continues) last.duration += item.duration;
    else out.push({ start: item.start, duration: item.duration, rest: item.rest });
    lastEvent = item.eventIndex;
  }
  return out;
}

const VALUE_NAME: Record<NoteValueCode, string> = { w: 'whole', h: 'half', q: 'quarter', '8': 'eighth', '16': 'sixteenth' };

/**
 * Spoken description of written rhythm for screen readers, measure by measure:
 * "Measure 1: quarter note, two eighth notes, eighth rest, dotted quarter note tied to…".
 */
export function describeRhythm(measures: EngravedMeasure[]): string {
  return measures
    .map((m) => {
      const words: string[] = [];
      let i = 0;
      while (i < m.items.length) {
        const group = m.tuplets.find((g) => g[0] === i);
        if (group) {
          // "eighth-note triplet" when all three are notes of one value, else "triplet (quarter note, eighth rest)".
          const parts = group.map((k) => m.items[k]!);
          const same = parts.length === 3 && parts.every((p) => !p.rest && p.code === parts[0]!.code);
          words.push(same ? `${VALUE_NAME[parts[0]!.code]}-note triplet` : `triplet (${parts.map((p) => `${VALUE_NAME[p.code]} ${p.rest ? 'rest' : 'note'}`).join(', ')})`);
          i += group.length;
          continue;
        }
        const item = m.items[i]!;
        const name = `${item.dots ? 'dotted ' : ''}${VALUE_NAME[item.code]} ${item.rest ? 'rest' : 'note'}`;
        let run = 1;
        while (
          !item.tieToNext &&
          i + run < m.items.length &&
          !m.items[i + run]!.tuplet &&
          (() => {
            const next = m.items[i + run]!;
            return next.code === item.code && next.dots === item.dots && next.rest === item.rest && !next.tieToNext && !next.tiedFromPrevious && !item.tiedFromPrevious;
          })()
        ) {
          run++;
        }
        const counted = run === 1 ? name : `${['', '', 'two', 'three', 'four', 'five', 'six'][run] ?? String(run)} ${name}s`;
        words.push(`${item.tiedFromPrevious ? 'tied to ' : ''}${counted}`);
        i += run;
      }
      return `Measure ${m.index + 1}: ${words.join(', ')}.`;
    })
    .join(' ');
}
